import { useState } from 'react';
import { createFileRoute, Link, Outlet, useMatchRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';

import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';

import { MISSION_STATUS_LABELS, MISSION_STATUS_VARIANT, SHIFT_BADGE_CLASS, SHIFT_LABELS } from '@/lib/missions';
import { useHasPermission } from '@/lib/permissions';
import { requirePermission } from '@/lib/route-guards';
import { cn } from '@/lib/utils';
import { QueryError } from '@/components/QueryError';

import type { Mission, MissionList } from '@logistech/ui-shared';

export const Route = createFileRoute('/_authed/missions')({
  beforeLoad: () => requirePermission('missions.read'),
  component: MissionsPage,
});

function MissionsPage() {
  const matchRoute = useMatchRoute();
  const isSubRoute = !!(matchRoute({ to: '/missions/new' }) || matchRoute({ to: '/missions/$id' }));
  if (isSubRoute) return <Outlet />;
  return <MissionsList />;
}

function MissionsList() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [shiftFilter, setShiftFilter] = useState('all');
  const canWrite = useHasPermission('missions.write');

  const query = useQuery({
    queryKey: ['missions', statusFilter, shiftFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '100' });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (shiftFilter !== 'all') params.set('shift', shiftFilter);
      return (await api.get<MissionList>(`/missions?${params.toString()}`)).data;
    },
  });
  const { data, isLoading } = query;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Missions</h1>
        {canWrite && (
          <Button asChild>
            <Link to="/missions/new">
              <Plus className="h-4 w-4 mr-2" /> Nouvelle mission
            </Link>
          </Button>
        )}
      </div>
      <QueryError query={query} />

      <div className="flex gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Tous les statuts" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(MISSION_STATUS_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={shiftFilter} onValueChange={setShiftFilter}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Shift" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les shifts</SelectItem>
            {Object.entries(SHIFT_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-card rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Origine → Destination</TableHead>
              <TableHead>Date prévue</TableHead>
              <TableHead>Shift</TableHead>
              <TableHead>Rotations</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                {Array.from({ length: 8 }).map((__, j) => (
                  <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                ))}
              </TableRow>
            ))}
            {data?.data?.map((m) => (
              <TableRow key={m.id}>
                <TableCell className="font-mono font-semibold">{m.reference}</TableCell>
                <TableCell>{m.client?.name ?? '—'}</TableCell>
                <TableCell className="text-sm">
                  {m.originSite?.name ?? '—'} → {m.destSite?.name ?? '—'}
                </TableCell>
                <TableCell>
                  {m.scheduledAt ? new Date(m.scheduledAt).toLocaleDateString('fr-FR') : '—'}
                </TableCell>
                <TableCell>
                  {m.shift && <Badge className={SHIFT_BADGE_CLASS[m.shift]}>{SHIFT_LABELS[m.shift]}</Badge>}
                </TableCell>
                <TableCell><RotationsCount mission={m} /></TableCell>
                <TableCell>
                  <Badge variant={MISSION_STATUS_VARIANT[m.status]}>{MISSION_STATUS_LABELS[m.status] ?? m.status}</Badge>
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/missions/$id" params={{ id: m.id }}>Détail</Link>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {data && (data.data?.length ?? 0) === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  Aucune mission trouvée
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function RotationsCount({ mission }: { mission: Mission }) {
  const count = mission.rotationsSummary?.count ?? 0;
  const target = mission.rotationsSummary?.targetRotations ?? null;
  if (count === 0 && target === null) return <span className="text-muted-foreground">— / —</span>;
  return (
    <span className={cn('tabular-nums', mission.rotationsSummary?.objectiveReached && 'font-bold text-success')}>
      {count} / {target ?? '—'}
    </span>
  );
}
